import { useState, useMemo, useRef, useCallback } from 'react'
import { Stack, TextField } from '@vapor/material'
import { Autocomplete } from '@mui/material'
import { FormItem } from 'hic-components'
import HicAmountField from '../../hic-amount-field/hic-amount-field'
import { useTenant } from 'src/app/hooks/context/context.hook'
import { PaymentAmount, PaymentError } from '../payment.types'
import { Payment } from '../payment-splitter/payment-splitter.types'
import { useTranslation } from 'react-i18next'
import { HICError } from '../../typography/error-text'

type PaymentSimpleInputProps = {
  readonly?: boolean
  disabled?: boolean
  error?: PaymentError
  payments: Payment[]
  value: PaymentAmount | null
  onChange: (changedValue: PaymentAmount | null) => void
  onAmountBlur?: () => void
  amountLabel?: string
}

export default function PaymentSimpleInput({
  //
  readonly,
  disabled,
  error,
  payments,
  value,
  onChange,
  onAmountBlur,
  amountLabel,
}: PaymentSimpleInputProps) {
  //
  const { t } = useTranslation()
  const { currency } = useTenant()
  const inputRef = useRef<HTMLInputElement>(null)

  const [open, setOpen] = useState(false)

  const paymentValue = useMemo(
    //
    () => {
      return value?.paymentActiveId ? payments.find((p) => p.paymentActiveId === value.paymentActiveId) || null : null
    },
    [value?.paymentActiveId, payments],
  )

  const onChangeHandler = useCallback(
    (changedValue: Partial<PaymentAmount>) => {
      if (value) onChange({ ...value, ...changedValue })
      else
        onChange({
          paymentActiveId: changedValue.paymentActiveId ?? null,
          amount: changedValue.amount ?? null,
          editable: changedValue.editable ?? false,
        })
    },
    [value, onChange],
  )

  return (
    <>
      <Stack direction="row" spacing={2} width={{ xs: '100%', sm: '80%', lg: '60%' }}>
        <FormItem //
          style={{ width: '60%' }}
          errorMessage={error?.paymentActiveId?.message}
          fieldLabel={t('pms.accounting.documents.payment_type')}
        >
          <Autocomplete
            fullWidth
            disabled={readonly || disabled}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            value={paymentValue}
            onChange={(event, v) => onChangeHandler({ paymentActiveId: v?.paymentActiveId })}
            options={payments}
            renderInput={(params) => {
              return <TextField {...params} error={error?.paymentActiveId ? true : false} inputRef={inputRef} />
            }}
            getOptionLabel={(option) => option.description}
            isOptionEqualToValue={(option, value) => option.paymentActiveId === value.paymentActiveId}
          />
        </FormItem>

        <FormItem //
          style={{ width: '40%' }}
          errorMessage={error?.amount?.message}
          fieldLabel={amountLabel || t('commons.paid_amount')}
        >
          <HicAmountField //
            fullWidth
            disabled={readonly || disabled}
            value={value?.amount}
            nonNegative
            currency={currency}
            onChange={(v) => onChangeHandler({ amount: v })}
            onBlur={onAmountBlur}
            error={error?.amount ? true : false}
          />
        </FormItem>
      </Stack>
      <HICError>{error && !error.paymentActiveId && !error.amount ? t('pms.accounting.documents.payment_type.validation_required') : undefined}</HICError>
    </>
  )
}
